import React, { type JSX } from 'react';
import { Link } from 'react-router';
import ModalRoute from './modal_route';
import getAriaProps from './get_aria_props';
import { withRouter } from '../hooks/withRouter';

/**
 * Represents the matched route state.
 */
interface RouteMatch {
  url: string;
  params: Record<string | number, unknown>;
}

/**
 * Represents the navigation history interface.
 */
interface NavigationHistory {
  push: (path: string) => void;
}

/**
 * Represents a location in the routing system.
 */
interface Location {
  pathname: string;
  search?: string;
  hash?: string;
  state?: Record<string, unknown>;
}

/**
 * Props for the ModalLink component, which combines a link and modal route.
 * Includes both user-provided props and withRouter-injected props.
 */
interface ModalLinkProps {
  // withRouter HOC injected props
  /** The current route match object (injected by withRouter HOC) */
  match: RouteMatch;
  /** The navigation history object (injected by withRouter HOC) */
  history: NavigationHistory;
  /** The current location (injected by withRouter HOC) */
  location: Location;

  // user-provided props
  /** Path to match in the route */
  path: string;
  /** Link text and content */
  children?: React.ReactNode;
  /** React component to render in the modal */
  component?: React.ComponentType<any>;
  /** Whether the route must match exactly */
  exact?: boolean;
  /** Props to be passed to the modal component */
  props?: Record<string, unknown>;
  /** CSS class name to apply to the modal container */
  modalClassName?: string;
  /** CSS class name to apply to the Link element */
  linkClassName?: string;
  /** HTML tabIndex for the Link element */
  tabIndex?: string | number;
  /** Path to navigate to when backdrop is clicked, or a function to compute it */
  parentPath?: string | ((match: RouteMatch) => string);
}

/**
 * Link and ModalRoute in one convenient component.
 * Renders a link that, when clicked, will navigate to the route that shows the modal.
 *
 * The component combines a react-router Link with a ModalRoute, providing a convenient
 * way to display modal dialogs via routing. It is wrapped with withRouter HOC to receive
 * match, history, and location props which are passed to the ModalRoute.
 *
 * @param props ModalLink configuration
 * @param props.path Path to match for showing the modal
 * @param props.exact If set, only show modal if route exactly matches path
 * @param props.parentPath Path to navigate to when backdrop is clicked, or a function that computes it
 * @param props.linkClassName CSS class name to apply to the Link element
 * @param props.modalClassName CSS class name to apply to the modal container
 * @param props.children Link text and content (displayed in the clickable link)
 * @param props.component React component to render inside the modal
 * @param props.props Additional props to pass to the modal component
 * @param props.tabIndex HTML tabIndex for keyboard navigation
 * @returns JSX.Element
 *
 * @example
 * <ModalLink
 *   path="/user/123/edit"
 *   component={EditUserForm}
 *   modalClassName="modal-container"
 *   linkClassName="edit-link"
 * >
 *   Edit User
 * </ModalLink>
 */
function ModalLink(props: ModalLinkProps): JSX.Element {
  const {
    exact,
    path,
    children,
    component,
    linkClassName,
    tabIndex,
    match,
    history,
    location,
    parentPath,
    modalClassName
  } = props;

  return (
    <Link to={path} className={linkClassName} tabIndex={typeof tabIndex === 'string' ? parseInt(tabIndex) : tabIndex}>
      {children}
      <ModalRoute
        exact={exact}
        path={path}
        match={match}
        history={history}
        location={location}
        props={props.props}
        component={component}
        className={modalClassName}
        parentPath={parentPath || match.url}
        {...getAriaProps(props)}
      />
    </Link>
  );
}

export default withRouter(ModalLink);
